import { inject } from "mobx-react";
import { useCallback, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { isPWSSessionBackend, parsePWSError } from "@inferno/renderer-shared-core";
import { ILog } from "@ihr-radioedit/inferno-core";
import { Button, ButtonKind, FauxButton } from "../../../../../core/ui";
import { FORM_ERRORS, SimpleInput } from "../../forms";
import { RecaptchaForm, RecaptchaFromOnSubmit } from "../../forms/RecaptchaForm.component";
import { useCoastAlerts } from "../common/coast-alert/CoastAlerts.hook";
import { CoastAlertsDisplay } from "../common/coast-alert/CoastAlertsDisplay.components";
import { UsersButton } from "../common/UsersButton.components";
import type { Store } from "@inferno/renderer-shared-core";

const log = ILog.logger("Coast Get Password Hint Component");

interface PasswordHintInputs {
  email: string;
}

interface CoastGetPasswordHintProps {
  goBack: () => void;
  email: string;
  store?: Store;
}

export const CoastGetPasswordHint = inject("store")(({ goBack, email, store }: CoastGetPasswordHintProps) => {
  const passwordHintFormMethods = useForm<PasswordHintInputs>({ defaultValues: { email } });
  const { register, errors } = passwordHintFormMethods;

  const { alerts, setErrorAlert, clearAllAlerts, setSuccessAlert } = useCoastAlerts();
  const [isProcessing, setIsProcessing] = useState(false);

  const session = store?.session?.currentSession;

  if (!store || !isPWSSessionBackend(session)) {
    return null;
  }

  const preSubmit = useCallback(() => {
    clearAllAlerts();
    setIsProcessing(true);
  }, [clearAllAlerts]);

  const onSubmit: RecaptchaFromOnSubmit<PasswordHintInputs> = useCallback(
    async ({ email: inputEmail }, token) => {
      try {
        const { passwordHint } = await session.memberService.getPasswordHint(inputEmail, token);
        setSuccessAlert(`Your password hint is: "${passwordHint}"`, 0);
      } catch (e) {
        log.error(e);
        setErrorAlert(parsePWSError(e));
      } finally {
        setIsProcessing(false);
      }
    },
    [setSuccessAlert, setErrorAlert, session.memberService],
  );

  return (
    <section className="component-forgot-password">
      <CoastAlertsDisplay alerts={alerts} />

      <FormProvider {...passwordHintFormMethods}>
        <RecaptchaForm
          siteKey={store.env.COAST_RECAPTCHA_V3_PUBLIC_KEY}
          setIsProcessing={setIsProcessing}
          setError={setErrorAlert}
          action="password_hint"
          preSubmit={preSubmit}
          onSubmit={onSubmit}
        >
          <SimpleInput
            placeholder="Enter your email"
            rules={{ required: FORM_ERRORS.required }}
            error={errors.email}
            register={register}
            label="Email"
            name="email"
            type="email"
          />

          <UsersButton
            disabled={!!errors.email || isProcessing}
            isProcessing={isProcessing}
            type="submit"
            label="Get Password Hint"
          >
            Get Password Hint
          </UsersButton>
        </RecaptchaForm>
      </FormProvider>

      <Button click={goBack} className="coast-button-text back-to-login" kind={ButtonKind.CUSTOMIZED}>
        Back To Login
      </Button>

      <p>
        Need Help? &nbsp;
        <FauxButton to="https://help.coasttocoastam.com/" className="coast-button-text" kind={ButtonKind.CUSTOMIZED}>
          Contact Customer Service
        </FauxButton>
      </p>
    </section>
  );
});
